import * as React from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import type { ConfigItem } from "../types";

type FormSchema = {
  value: string;
};

const schema = z.object({
  value: z.string().max(255, "Máximo de 255 caracteres"),
});

type EditConfigDialogProps = {
  config: ConfigItem;
  isSaving?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSubmit: (value: string) => Promise<void>;
  onCancel: () => void;
};

export const EditConfigDialog: React.FC<EditConfigDialogProps> = ({
  config,
  isSaving = false,
  onOpenChange,
  onSubmit,
  onCancel,
}) => {
  const form = useForm<FormSchema>({
    resolver: zodResolver(schema),
    defaultValues: { value: config.value ?? "" },
  });

  React.useEffect(() => {
    form.reset({ value: config.value ?? "" });
  }, [config.value, form]);

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      onOpenChange?.(open);
      if (!open) {
        onCancel();
      }
    },
    [onCancel, onOpenChange],
  );

  const handleSubmit = form.handleSubmit(async ({ value }) => {
    await onSubmit(value);
    form.reset({ value });
  });

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar configuração</DialogTitle>
          <DialogDescription>
            Atualize o valor da configuração "{config.key}".
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <FormField
              control={form.control}
              name="value"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Valor</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      autoFocus
                      aria-label={`Editar valor para ${config.key}`}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="ghost"
                onClick={onCancel}
                disabled={isSaving}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? "A guardar…" : "Guardar"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
